import { AxiosError } from 'axios';
import { ServerResponse, WithAuditDetails } from 'data/types';
import { History } from 'history';
import { Dispatch, Action } from 'redux';
import { RootState } from 'data/root.reducers';
import i18n from 'i18n';
import { showSuccessMessage, showError } from 'shared/toaster';
import { ThunkDispatch } from 'redux-thunk';
import { extractErrorMessage } from '../../utils';
import * as api from './snmp.api';
import {
  GO_TO_SNMP_USERS_LIST,
  GO_TO_SNMP_USERS_CREATE,
  GO_TO_SNMP_USERS_EDIT,
  GO_TO_SNMP_USERS_VIEW,
  SnmpActionTypes,
  GET_SNMP_USERS,
  GET_SNMP_USER,
  SELECT_SNMP_USER,
  SnmpV3User,
  CreateSnmpUserThunkAction,
  CREATE_SNMP_USER,
  DeleteSnmpUserThunkAction,
  DELETE_SNMP_USER,
  UpdateSnmpUserThunkAction,
  UPDATE_SNMP_USER,
  SelectSnmpUserAction,
  GO_TO_SNMP_DESTS_LIST,
  GO_TO_SNMP_DESTS_CREATE,
  GO_TO_SNMP_DESTS_EDIT,
  GO_TO_SNMP_DESTS_VIEW,
  GET_SNMP_DESTS,
  GET_SNMP_DEST,
  SnmpDestination,
  DELETE_SNMP_DEST,
  UPDATE_SNMP_DEST,
  SELECT_SNMP_DEST,
  SelectSnmpDestAction

} from './snmp.types';
import {
  SYSTEM_CONFIGURATION_ROUTE,
  SECURITY,
  SNMP_USERS,
  SNMP_DEST,
  NETWORK
} from '../../constants';

export function goToSmnpUserList(
  history: History
): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_SNMP_USERS_LIST
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${SECURITY}${SNMP_USERS}`);
  };
}

export function goToSnmpUserCreate(
  history: History
): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_SNMP_USERS_CREATE
    });

    history.push(
      `${SYSTEM_CONFIGURATION_ROUTE}${SECURITY}${SNMP_USERS}/create`
    );
  };
}

export function goToSnmpUserEdit(
  history: History
): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_SNMP_USERS_EDIT
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${SECURITY}${SNMP_USERS}/edit`);
  };
}

export function goToSnmpUserView(
  history: History
): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_SNMP_USERS_VIEW
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${SECURITY}${SNMP_USERS}/view`);
  };
}

export function getSnmpUsers(deliveryFunctionId: string): SnmpActionTypes {
  return {
    type: GET_SNMP_USERS,
    payload: api.getSnmpUsers(deliveryFunctionId)
  };
}

export function getSnmpUser(
  deliveryFunctionId: string,
  id: string
): SnmpActionTypes {
  return {
    type: GET_SNMP_USER,
    payload: api.getSnmpUserById(deliveryFunctionId, id)
  };
}

export function createSnmpUser(
  deliveryFunctionId: string,
  body: WithAuditDetails<SnmpV3User>,
  history: History
): CreateSnmpUserThunkAction {
  return async (
    dispatch: ThunkDispatch<RootState, unknown, Action<string>>
  ) => {
    try {
      await dispatch({
        type: CREATE_SNMP_USER,
        payload: api.createSnmpUser(deliveryFunctionId, body)
      });

      dispatch(getSnmpUsers(deliveryFunctionId));
      dispatch(goToSmnpUserList(history));
      showSuccessMessage(
        i18n.t('snmpUser.create.success', { name: body.data?.USERNAME }),
        5000
      );
    } catch (e) {
      const errorMessage = extractErrorMessage(
        e as AxiosError<ServerResponse<void>>
      );
      showError(
        i18n.t('snmpUser.create.failed', {
          name: body.data?.USERNAME,
          error: errorMessage
        })
      );
    }
  };
}

export function deleteSnmpUser(
  deliveryFunctionId: string,
  body: WithAuditDetails<void>,
  selectedSnmpUser: SnmpV3User,
  history?: History
): DeleteSnmpUserThunkAction {
  return async (
    dispatch: ThunkDispatch<RootState, unknown, Action<string>>,
    getState: () => RootState
  ) => {
    try {
      await dispatch({
        type: DELETE_SNMP_USER,
        payload: api.deleteSnmpUserById(
          deliveryFunctionId,
          selectedSnmpUser.id,
          body
        )
      });
      showSuccessMessage(
        i18n.t('snmpUser.delete.success', { name: selectedSnmpUser?.USERNAME }),
        5000
      );
      if (history != null) {
        dispatch(goToSmnpUserList(history));
      }
    } catch (e) {
      showError(
        i18n.t('snmpUser.delete.failed', { name: selectedSnmpUser?.USERNAME })
      );
    } finally {
      dispatch(getSnmpUsers(deliveryFunctionId));
    }
  };
}

export function updateSnmpUser(
  deliveryFunctionId: string,
  body: WithAuditDetails<SnmpV3User>,
  history: History
): UpdateSnmpUserThunkAction {
  return async (
    dispatch: ThunkDispatch<RootState, unknown, Action<string>>
  ) => {
    try {
      await dispatch({
        type: UPDATE_SNMP_USER,
        payload: api.updateSnmpUser(deliveryFunctionId, body.data.id, body)
      });

      dispatch(goToSmnpUserList(history));
      showSuccessMessage(
        i18n.t('snmpUser.update.success', { id: body.data?.id }),
        5000
      );
    } catch (e) {
      const errorMessage = extractErrorMessage(
        e as AxiosError<ServerResponse<void>>
      );
      showError(
        i18n.t('snmpUser.update.failed', {
          id: body.data?.id,
          error: errorMessage
        })
      );
    }
  };
}

export function selectSnmpUser(
  snmpUser: SnmpV3User,
): SelectSnmpUserAction {
  return {
    type: SELECT_SNMP_USER,
    payload: snmpUser,
  };
}

export function goToSmnpDestList(
  history: History
): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_SNMP_DESTS_LIST
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${NETWORK}${SNMP_DEST}`);
  };
}

export function goToSnmpDestCreate(
  history: History
): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_SNMP_DESTS_CREATE
    });

    history.push(
      `${SYSTEM_CONFIGURATION_ROUTE}${NETWORK}${SNMP_DEST}/create`
    );
  };
}

export function goToSnmpDestEdit(
  history: History
): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_SNMP_DESTS_EDIT
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${NETWORK}${SNMP_DEST}/edit`);
  };
}

export function goToSnmpDestView(
  history: History
): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_SNMP_DESTS_VIEW
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${NETWORK}${SNMP_DEST}/view`);
  };
}

export function getSnmpDests(deliveryFunctionId: string): SnmpActionTypes {
  return {
    type: GET_SNMP_DESTS,
    payload: api.getSnmpDests(deliveryFunctionId)
  };
}

export function getSnmpDest(
  deliveryFunctionId: string,
  id: string
): SnmpActionTypes {
  return {
    type: GET_SNMP_DEST,
    payload: api.getSnmpDestById(deliveryFunctionId, id)
  };
}

export function createSnmpDest(
  deliveryFunctionId: string,
  body: WithAuditDetails<SnmpDestination>,
  history: History
): CreateSnmpUserThunkAction {
  return async (
    dispatch: ThunkDispatch<RootState, unknown, Action<string>>
  ) => {
    try {
      await dispatch({
        type: CREATE_SNMP_USER,
        payload: api.createSnmpDest(deliveryFunctionId, body)
      });

      dispatch(getSnmpDests(deliveryFunctionId));
      dispatch(goToSmnpDestList(history));
      showSuccessMessage(
        i18n.t('snmpDest.create.success', { name: body.data?.NAME }),
        5000
      );
    } catch (e) {
      const errorMessage = extractErrorMessage(
        e as AxiosError<ServerResponse<void>>
      );
      showError(
        i18n.t('snmpDest.create.failed', {
          name: body.data?.NAME,
          error: errorMessage
        })
      );
    }
  };
}

export function deleteSnmpDest(
  deliveryFunctionId: string,
  body: WithAuditDetails<void>,
  selectedSnmpDest: SnmpDestination,
  history?: History
): DeleteSnmpUserThunkAction {
  return async (
    dispatch: ThunkDispatch<RootState, unknown, Action<string>>,
    getState: () => RootState
  ) => {
    try {
      await dispatch({
        type: DELETE_SNMP_DEST,
        payload: api.deleteSnmpDestById(
          deliveryFunctionId,
          selectedSnmpDest.id,
          body
        )
      });
      showSuccessMessage(
        i18n.t('snmpDest.delete.success', { name: selectedSnmpDest?.NAME }),
        5000
      );
      if (history != null) {
        dispatch(goToSmnpDestList(history));
      }
    } catch (e) {
      showError(
        i18n.t('snmpDest.delete.failed', { name: selectedSnmpDest?.NAME })
      );
    } finally {
      dispatch(getSnmpDests(deliveryFunctionId));
    }
  };
}

export function updateSnmpDest(
  deliveryFunctionId: string,
  body: WithAuditDetails<SnmpDestination>,
  history: History
): UpdateSnmpUserThunkAction {
  return async (
    dispatch: ThunkDispatch<RootState, unknown, Action<string>>
  ) => {
    try {
      await dispatch({
        type: UPDATE_SNMP_DEST,
        payload: api.updateSnmpDest(deliveryFunctionId, body.data.id, body)
      });

      dispatch(goToSmnpDestList(history));
      showSuccessMessage(
        i18n.t('snmpDest.update.success', { id: body.data?.id }),
        5000
      );
    } catch (e) {
      const errorMessage = extractErrorMessage(
        e as AxiosError<ServerResponse<void>>
      );
      showError(
        i18n.t('snmpDest.update.failed', {
          id: body.data?.id,
          error: errorMessage
        })
      );
    }
  };
}

export function selectSnmpDest(
  snmpDest: SnmpDestination,
): SelectSnmpDestAction {
  return {
    type: SELECT_SNMP_DEST,
    payload: snmpDest,
  };
}
